import { useState, useEffect } from "react";
import { useAuth, apiClient } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { DeleteConfirmDialog } from "../components/ui/delete-confirm-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { DateInput } from "../components/ui/date-input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { format } from "date-fns";
import { 
  LogOut, Plus, Users, MapPin, CreditCard, Calendar as CalendarIcon, 
  Trash2, Cake, FileText, Heart, MessageCircle, ChevronDown, Wallet,
  TrendingUp, TrendingDown, DollarSign, PiggyBank, BarChart3, Pencil, Mail, CheckCircle, AlertCircle, Trophy, ExternalLink, History, CheckCircle2, Sparkles, Download, FileSpreadsheet
} from "lucide-react";
import ScoreRecordingModal from "../components/ScoreRecordingModal";
import PlayerStatsWidget from "../components/PlayerStatsWidget";
import LedgerDownloadButton from "../components/LedgerDownloadButton";
import AuditLogTab from "../components/AuditLogTab";
import SettleBalanceDialog from "../components/SettleBalanceDialog";
import SpecialitiesTab from "../components/SpecialitiesTab";
import AttendeeSelector from "../components/AttendeeSelector";
import MemberSearchSelect from "../components/MemberSearchSelect";

const memberPhotoUrl = (userId) =>
  `${process.env.REACT_APP_BACKEND_URL}/api/users/${userId}/photo?token=${localStorage.getItem("turf_tribe_token")}`;

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [users, setUsers] = useState([]);
  const [turfLocations, setTurfLocations] = useState([]);
  const [payments, setPayments] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [posts, setPosts] = useState([]);
  const [upcomingBirthdays, setUpcomingBirthdays] = useState([]);
  const [financialSummary, setFinancialSummary] = useState(null);
  const [emailStatus, setEmailStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  // Modal states
  const [showAddUser, setShowAddUser] = useState(false);
  const [showAddLocation, setShowAddLocation] = useState(false);
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [showAddSession, setShowAddSession] = useState(false);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [showAddPost, setShowAddPost] = useState(false);
  const [showUserDetail, setShowUserDetail] = useState(false);
  const [selectedUserDetail, setSelectedUserDetail] = useState(null);
  const [loadingUserDetail, setLoadingUserDetail] = useState(false);
  const [settleUser, setSettleUser] = useState(null);
  
  // Score recording modal state
  const [showScoreModal, setShowScoreModal] = useState(false);
  const [selectedSessionForScores, setSelectedSessionForScores] = useState(null);
  
  // Edit modal states
  const [showEditUser, setShowEditUser] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showEditLocation, setShowEditLocation] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [showEditPayment, setShowEditPayment] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [showEditSession, setShowEditSession] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [showEditPost, setShowEditPost] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  
  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState({
    open: false,
    type: null,
    id: null,
    name: "",
    title: "",
    description: ""
  });
  
  // User selection for export
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [exportingUsers, setExportingUsers] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, []);

  // User selection handlers for export
  const toggleUserSelection = (userId) => {
    setSelectedUserIds(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId) 
        : [...prev, userId]
    );
  };

  const toggleSelectAllUsers = () => {
    if (selectedUserIds.length === users.length) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(users.map(u => u.id));
    }
  };

  const handleExportUsers = async (format) => {
    if (selectedUserIds.length === 0) {
      toast.error("Please select at least one user to export");
      return;
    }
    setExportingUsers(true);
    try {
      const endpoint = format === 'pdf' 
        ? '/admin/users/export/pdf' 
        : '/admin/users/export/excel';
      
      const response = await apiClient.post(endpoint, { user_ids: selectedUserIds }, {
        responseType: 'blob'
      });
      
      // Create download link with proper MIME type
      const mimeType = format === 'pdf' 
        ? 'application/pdf' 
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const blob = new Blob([response.data], { type: mimeType });
      const filename = `turf_tribe_members_${new Date().toISOString().split('T')[0]}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      
      // Create object URL
      const url = window.URL.createObjectURL(blob);
      
      // Create and configure download link
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.cssText = 'position:fixed;left:-9999px;top:-9999px;';
      
      // Add to DOM
      document.body.appendChild(link);
      
      // Trigger download
      link.click();
      
      // Cleanup with delay
      requestAnimationFrame(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      });
      
      toast.success(`Downloaded ${filename}`);
    } catch (error) {
      toast.error("Failed to export users");
      console.error("Export error:", error);
    } finally {
      setExportingUsers(false);
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [usersRes, locationsRes, paymentsRes, sessionsRes, expensesRes, postsRes, birthdaysRes, financialRes, emailRes] = await Promise.all([
        apiClient.get("/users"),
        apiClient.get("/turf-locations"),
        apiClient.get("/payments"),
        apiClient.get("/sessions"),
        apiClient.get("/expenses"),
        apiClient.get("/posts"),
        apiClient.get("/birthdays/upcoming"),
        apiClient.get("/admin/financial-summary"),
        apiClient.get("/admin/email-status"),
      ]);
      setUsers(usersRes.data);
      setTurfLocations(locationsRes.data);
      setPayments(paymentsRes.data);
      setSessions(sessionsRes.data);
      setExpenses(expensesRes.data);
      setPosts(postsRes.data);
      setUpcomingBirthdays(birthdaysRes.data);
      setFinancialSummary(financialRes.data);
      setEmailStatus(emailRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  // Add User
  const AddUserModal = () => {
    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");
    const [email, setEmail] = useState("");
    const [dob, setDob] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async () => {
      if (!name || !phone) {
        toast.error("Name and phone are required");
        return;
      }
      setSubmitting(true);
      try {
        await apiClient.post("/users", {
          name,
          phone,
          email: email || null,
          date_of_birth: dob ? format(dob, "yyyy-MM-dd") : null,
        });
        toast.success("User added successfully");
        setShowAddUser(false);
        fetchAllData();
      } catch (error) {
        toast.error(error.response?.data?.detail || "Failed to add user");
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <Dialog open={showAddUser} onOpenChange={setShowAddUser}>
        <DialogContent className="sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Add New Member</DialogTitle>
            <DialogDescription>Add a new member to Turf Tribe</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                data-testid="add-user-name-input"
                placeholder="Enter member name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-11 rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label>Phone Number *</Label>
              <Input
                data-testid="add-user-phone-input"
                placeholder="Enter phone number"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="h-11 rounded-xl"
              />
              <p className="text-xs text-slate-500">This will also be the password</p>
            </div>
            <div className="space-y-2">
              <Label>Email (Optional)</Label>
              <Input
                data-testid="add-user-email-input"
                type="email"
                placeholder="Enter email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-11 rounded-xl"
              />
              <p className="text-xs text-slate-500">For future notifications</p>
            </div>
            <div className="space-y-2">
              <Label>Date of Birth (Optional)</Label>
              <DateInput
                value={dob}
                onChange={setDob}
                max={new Date()}
                data-testid="add-user-dob-input"
              />
            </div>
          </div>
          <Button
            data-testid="add-user-submit-btn"
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full h-11 bg-[#064E3B] hover:bg-[#065f46] rounded-xl font-bold"
          >
            {submitting ? "Adding..." : "Add Member"}
          </Button>
        </DialogContent>
      </Dialog>
    );
  };

  // Edit User Modal
  const EditUserModal = () => {
    const [name, setName] = useState(editingUser?.name || "");
    const [phone, setPhone] = useState(editingUser?.phone || "");
    const [email, setEmail] = useState(editingUser?.email || "");
    const [dob, setDob] = useState(editingUser?.date_of_birth ? new Date(editingUser.date_of_birth) : null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
      if (editingUser) {
        setName(editingUser.name || "");
        setPhone(editingUser.phone || "");
        setEmail(editingUser.email || "");
        setDob(editingUser.date_of_birth ? new Date(editingUser.date_of_birth) : null);
      }
    }, [editingUser]);

    const handleSubmit = async () => {
      if (!name || !phone) {
        toast.error("Name and phone are required");
        return;
      }
      setSubmitting(true);
      try {
        await apiClient.put(`/users/${editingUser.id}`, {
          name,
          phone,
          email: email || null,
          date_of_birth: dob ? format(dob, "yyyy-MM-dd") : null,
        });
        toast.success("User updated successfully");
        setShowEditUser(false);
        setEditingUser(null);
        fetchAllData();
      } catch (error) {
        toast.error(error.response?.data?.detail || "Failed to update user");
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <Dialog open={showEditUser} onOpenChange={(open) => { setShowEditUser(open); if (!open) setEditingUser(null); }}>
        <DialogContent className="sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Edit Member</DialogTitle>
            <DialogDescription>Update member details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                data-testid="edit-user-name-input"
                placeholder="Enter member name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-11 rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label>Phone Number *</Label>
              <Input
                data-testid="edit-user-phone-input"
                placeholder="Enter phone number"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="h-11 rounded-xl"
              />
              <p className="text-xs text-slate-500">Changing phone will update password too</p>
            </div>
            <div className="space-y-2">
              <Label>Email (Optional)</Label>
              <Input
                data-testid="edit-user-email-input"
                type="email"
                placeholder="Enter email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-11 rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label>Date of Birth (Optional)</Label>
              <DateInput
                value={dob}
                onChange={setDob}
                max={new Date()}
                data-testid="edit-user-dob-input"
              />
            </div>
          </div>
          <Button
            data-testid="edit-user-submit-btn"
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full h-11 bg-[#064E3B] hover:bg-[#065f46] rounded-xl font-bold"
          >
            {submitting ? "Updating..." : "Update Member"}
          </Button>
        </DialogContent>
      </Dialog>
    );
  };

  // Add Location
  const AddLocationModal = () => {
    const [name, setName] = useState("");
    const [googleMapsUrl, setGoogleMapsUrl] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async () => {
      if (!name) {
        toast.error("Location name is required");
        return;
      }
      setSubmitting(true);
      try {
        await apiClient.post("/turf-locations", { name, google_maps_url: googleMapsUrl || null });
        toast.success("Location added successfully");
        setShowAddLocation(false);
        fetchAllData();
      } catch (error) {
        toast.error("Failed to add location");
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <Dialog open={showAddLocation} onOpenChange={setShowAddLocation}>
        <DialogContent className="sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Add Turf Location</DialogTitle>
            <DialogDescription>Add a new pickleball turf location</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Location Name</Label>
              <Input
                data-testid="add-location-name-input"
                placeholder="e.g., Dombivli Sports Complex"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-11 rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label>Google Maps URL (Optional)</Label>
              <Input
                data-testid="add-location-maps-input"
                placeholder="https://maps.google.com/..."
                value={googleMapsUrl}
                onChange={(e) => setGoogleMapsUrl(e.target.value)}
                className="h-11 rounded-xl"
              />
            </div>
          </div>
          <Button
            data-testid="add-location-submit-btn"
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full h-11 bg-[#064E3B] hover:bg-[#065f46] rounded-xl font-bold"
          >
            {submitting ? "Adding..." : "Add Location"}
          </Button>
        </DialogContent>
      </Dialog>
    );
  };

  // Edit Location Modal
  const EditLocationModal = () => {
    const [name, setName] = useState(editingLocation?.name || "");
    const [googleMapsUrl, setGoogleMapsUrl] = useState(editingLocation?.google_maps_url || "");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
      if (editingLocation) {
        setName(editingLocation.name || "");
        setGoogleMapsUrl(editingLocation.google_maps_url || "");
      }
    }, [editingLocation]);

    const handleSubmit = async () => {
      if (!name) {
        toast.error("Location name is required");
        return;
      }
      setSubmitting(true);
      try {
        await apiClient.put(`/turf-locations/${editingLocation.id}`, { name, google_maps_url: googleMapsUrl || null });
        toast.success("Location updated successfully");
        setShowEditLocation(false);
        setEditingLocation(null);
        fetchAllData();
      } catch (error) {
        toast.error("Failed to update location");
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <Dialog open={showEditLocation} onOpenChange={(open) => { setShowEditLocation(open); if (!open) setEditingLocation(null); }}>
        <DialogContent className="sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Edit Location</DialogTitle>
            <DialogDescription>Update turf location details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Location Name</Label>
              <Input
                data-testid="edit-location-name-input"
                placeholder="e.g., Dombivli Sports Complex"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-11 rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label>Google Maps URL (Optional)</Label>
              <Input
                data-testid="edit-location-maps-input"
                placeholder="https://maps.google.com/..."
                value={googleMapsUrl}
                onChange={(e) => setGoogleMapsUrl(e.target.value)}
                className="h-11 rounded-xl"
              />
            </div>
          </div>
          <Button
            data-testid="edit-location-submit-btn"
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full h-11 bg-[#064E3B] hover:bg-[#065f46] rounded-xl font-bold"
          >
            {submitting ? "Updating..." : "Update Location"}
          </Button>
        </DialogContent>
      </Dialog>
    );
  };

  // Add Payment
  const AddPaymentModal = () => {
    const [userId, setUserId] = useState("");
    const [amount, setAmount] = useState("");
    const [paymentMode, setPaymentMode] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async () => {
      if (!userId || !amount || !paymentMode) {
        toast.error("All fields are required");
        return;
      }
      setSubmitting(true);
      try {
        await apiClient.post("/payments", {
          user_id: userId,
          amount: parseFloat(amount),
          payment_mode: paymentMode,
        });
        toast.success("Payment recorded successfully");
        setShowAddPayment(false);
        fetchAllData();
      } catch (error) {
        toast.error("Failed to record payment");
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <Dialog open={showAddPayment} onOpenChange={setShowAddPayment}>
        <DialogContent className="sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Record Payment</DialogTitle>
            <DialogDescription>Record a payment received from a member</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Member</Label>
              <MemberSearchSelect
                users={users}
                value={userId}
                onChange={setUserId}
                testId="payment-user-select"
              />
            </div>
            <div className="space-y-2">
              <Label>Amount (₹)</Label>
              <Input
                data-testid="payment-amount-input"
                type="number"
                placeholder="Enter amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="h-11 rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label>Payment Mode</Label>
              <Select value={paymentMode} onValueChange={setPaymentMode}>
                <SelectTrigger data-testid="payment-mode-select" className="h-11 rounded-xl">
                  <SelectValue placeholder="Select payment mode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Google Pay">Google Pay</SelectItem>
                  <SelectItem value="Cash">Cash</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button
            data-testid="payment-submit-btn"
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full h-11 bg-[#064E3B] hover:bg-[#065f46] rounded-xl font-bold"
          >
            {submitting ? "Recording..." : "Record Payment"}
          </Button>
        </DialogContent>
      </Dialog>
    );
  };

  // Edit Payment Modal
  const EditPaymentModal = () => {
    const [amount, setAmount] = useState(editingPayment?.amount?.toString() || "");
    const [paymentMode, setPaymentMode] = useState(editingPayment?.payment_mode || "");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
      if (editingPayment) {
        setAmount(editingPayment.amount?.toString() || "");
        setPaymentMode(editingPayment.payment_mode || "");
      }
    }, [editingPayment]);

    const handleSubmit = async () => {
      if (!amount || !paymentMode) {
        toast.error("All fields are required");
        return;
      }
      setSubmitting(true);
      try {
        await apiClient.put(`/payments/${editingPayment.id}`, {
          amount: parseFloat(amount),
          payment_mode: paymentMode,
        });
        toast.success("Payment updated successfully");
        setShowEditPayment(false);
        setEditingPayment(null);
        fetchAllData();
      } catch (error) {
        toast.error("Failed to update payment");
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <Dialog open={showEditPayment} onOpenChange={(open) => { setShowEditPayment(open); if (!open) setEditingPayment(null); }}>
        <DialogContent className="sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Edit Payment</DialogTitle>
            <DialogDescription>Update payment for {editingPayment?.user_name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Amount (₹)</Label>
              <Input
                data-testid="edit-payment-amount-input"
                type="number"
                placeholder="Enter amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="h-11 rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label>Payment Mode</Label>
              <Select value={paymentMode} onValueChange={setPaymentMode}>
                <SelectTrigger data-testid="edit-payment-mode-select" className="h-11 rounded-xl">
                  <SelectValue placeholder="Select payment mode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Google Pay">Google Pay</SelectItem>
                  <SelectItem value="Cash">Cash</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button
            data-testid="edit-payment-submit-btn"
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full h-11 bg-[#064E3B] hover:bg-[#065f46] rounded-xl font-bold"
          >
            {submitting ? "Updating..." : "Update Payment"}
          </Button>
        </DialogContent>
      </Dialog>
    );
  };

  // Add Session
  const AddSessionModal = () => {
    const [date, setDate] = useState(null);
    const [locationId, setLocationId] = useState("");
    const [amount, setAmount] = useState("");
    const [attendees, setAttendees] = useState({}); // { usedId: { selected: bool, guests: number } }
    const [submitting, setSubmitting] = useState(false);

    // Initialize attendees state when users change
    useEffect(() => {
      const initial = {};
      users.forEach(u => {
        initial[u.id] = { selected: false, guests: 0 };
      });
      setAttendees(initial);
    }, [users]);

    const toggleAttendee = (userId) => {
      setAttendees(prev => ({
        ...prev,
        [userId]: { 
          ...prev[userId], 
          selected: !prev[userId]?.selected,
          guests: !prev[userId]?.selected ? 0 : prev[userId]?.guests || 0
        }
      }));
    };

    const setGuestCount = (userId, count) => {
      const guestCount = Math.max(0, parseInt(count) || 0);
      setAttendees(prev => ({
        ...prev,
        [userId]: { ...prev[userId], guests: guestCount }
      }));
    };

    const setMany = (ids, selected) => {
      setAttendees(prev => {
        const updated = { ...prev };
        ids.forEach(id => {
          updated[id] = { selected, guests: selected ? (prev[id]?.guests || 0) : 0 };
        });
        return updated;
      });
    };

    const selectedCount = Object.values(attendees).filter(a => a?.selected).length;
    const totalGuests = Object.values(attendees).reduce((sum, a) => sum + (a?.selected ? (a?.guests || 0) : 0), 0);
    const totalHeads = selectedCount + totalGuests;

    const handleSubmit = async () => {
      if (!date || !locationId || !amount || selectedCount === 0) {
        toast.error("All fields are required and select at least one attendee");
        return;
      }
      setSubmitting(true);
      try {
        const attendeesList = Object.entries(attendees)
          .filter(([_, data]) => data?.selected)
          .map(([userId, data]) => ({
            user_id: userId,
            guest_count: data?.guests || 0
          }));

        await apiClient.post("/sessions", {
          date: format(date, "yyyy-MM-dd"),
          turf_location_id: locationId,
          total_amount: parseFloat(amount),
          attendees: attendeesList,
        });
        toast.success("Session created successfully");
        setShowAddSession(false);
        fetchAllData();
      } catch (error) {
        toast.error(error.response?.data?.detail || "Failed to create session");
      } finally {
        setSubmitting(false);
      }
    };

    const perPersonPreview = totalHeads > 0 && amount ? (parseFloat(amount) / totalHeads).toFixed(2) : "0.00";

    return (
      <Dialog open={showAddSession} onOpenChange={setShowAddSession}>
        <DialogContent className="sm:max-w-md rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Create Session</DialogTitle>
            <DialogDescription>Create a new pickleball session</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Date</Label>
              <DateInput
                value={date}
                onChange={setDate}
                data-testid="session-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Turf Location</Label>
              <Select value={locationId} onValueChange={setLocationId}>
                <SelectTrigger data-testid="session-location-select" className="h-11 rounded-xl">
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {turfLocations.map((loc) => (
                    <SelectItem key={loc.id} value={loc.id}>
                      {loc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Total Amount (₹)</Label>
              <Input
                data-testid="session-amount-input"
                type="number"
                placeholder="Enter total amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="h-11 rounded-xl"
              />
            </div>
            <AttendeeSelector
              users={users}
              attendees={attendees}
              label={`Attendees (${selectedCount} members${totalGuests > 0 ? ` + ${totalGuests} guests` : ''})`}
              onToggle={toggleAttendee}
              onGuestChange={setGuestCount}
              onSetMany={setMany}
              testIdPrefix="session"
            />
            
            {/* Preview calculation */}
            {totalHeads > 0 && amount && (
              <div className="bg-slate-50 rounded-xl p-3 space-y-1">
                <p className="text-xs text-slate-500 font-medium">Cost Preview</p>
                <p className="text-sm">
                  Total: ₹{amount} ÷ {totalHeads} heads = <strong>₹{perPersonPreview}/person</strong>
                </p>
                {totalGuests > 0 && (
                  <p className="text-xs text-slate-500">
                    Members bringing guests will pay for themselves + their guests
                  </p>
                )}
              </div>
            )}
          </div>
          <Button
            data-testid="session-submit-btn"
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full h-11 bg-[#064E3B] hover:bg-[#065f46] rounded-xl font-bold"
          >
            {submitting ? "Creating..." : "Create Session"}
          </Button>
        </DialogContent>
      </Dialog>
    );
  };

  // Edit Session Modal
  const EditSessionModal = () => {
    const [date, setDate] = useState(null);
    const [locationId, setLocationId] = useState("");
    const [amount, setAmount] = useState("");
    const [attendees, setAttendees] = useState({});
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
      if (editingSession) {
        setDate(editingSession.date ? new Date(editingSession.date) : null);
        setLocationId(editingSession.turf_location_id || "");
        setAmount(editingSession.total_amount?.toString() || "");
        
        // Initialize attendees from session detail
        const initial = {};
        users.forEach(u => {
          const att = editingSession.attendees_detail?.find(a => a.user_id === u.id);
          initial[u.id] = { 
            selected: !!att, 
            guests: att?.guest_count || 0 
          };
        });
        setAttendees(initial);
      }
    }, [editingSession, users]);

    const toggleAttendee = (userId) => {
      setAttendees(prev => ({
        ...prev,
        [userId]: { 
          ...prev[userId], 
          selected: !prev[userId]?.selected,
          guests: !prev[userId]?.selected ? 0 : prev[userId]?.guests || 0
        }
      }));
    };

    const setGuestCount = (userId, count) => {
      const guestCount = Math.max(0, parseInt(count) || 0);
      setAttendees(prev => ({
        ...prev,
        [userId]: { ...prev[userId], guests: guestCount }
      }));
    };

    const setMany = (ids, selected) => {
      setAttendees(prev => {
        const updated = { ...prev };
        ids.forEach(id => {
          updated[id] = { selected, guests: selected ? (prev[id]?.guests || 0) : 0 };
        });
        return updated;
      });
    };

    const selectedCount = Object.values(attendees).filter(a => a?.selected).length;
    const totalGuests = Object.values(attendees).reduce((sum, a) => sum + (a?.selected ? (a?.guests || 0) : 0), 0);
    const totalHeads = selectedCount + totalGuests;
    const perPersonPreview = totalHeads > 0 && amount ? (parseFloat(amount) / totalHeads).toFixed(2) : "0.00";

    const handleSubmit = async () => {
      if (!date || !locationId || !amount || selectedCount === 0) {
        toast.error("All fields are required");
        return;
      }
      setSubmitting(true);
      try {
        const attendeesList = Object.entries(attendees)
          .filter(([_, data]) => data?.selected)
          .map(([userId, data]) => ({
            user_id: userId,
            guest_count: data?.guests || 0
          }));

        await apiClient.put(`/sessions/${editingSession.id}`, {
          date: format(date, "yyyy-MM-dd"),
          turf_location_id: locationId,
          total_amount: parseFloat(amount),
          attendees: attendeesList,
        });
        toast.success("Session updated successfully");
        setShowEditSession(false);
        setEditingSession(null);
        fetchAllData();
      } catch (error) {
        toast.error(error.response?.data?.detail || "Failed to update session");
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <Dialog open={showEditSession} onOpenChange={(open) => { setShowEditSession(open); if (!open) setEditingSession(null); }}>
        <DialogContent className="sm:max-w-md rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Edit Session</DialogTitle>
            <DialogDescription>Update session details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Date</Label>
              <DateInput
                value={date}
                onChange={setDate}
                data-testid="edit-session-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Turf Location</Label>
              <Select value={locationId} onValueChange={setLocationId}>
                <SelectTrigger className="h-11 rounded-xl">
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {turfLocations.map((loc) => (
                    <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Total Amount (₹)</Label>
              <Input
                type="number"
                placeholder="Enter total amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="h-11 rounded-xl"
              />
            </div>
            <AttendeeSelector
              users={users}
              attendees={attendees}
              label={`Attendees (${selectedCount} members${totalGuests > 0 ? ` + ${totalGuests} guests` : ''})`}
              onToggle={toggleAttendee}
              onGuestChange={setGuestCount}
              onSetMany={setMany}
              testIdPrefix="edit-session"
            />
            {totalHeads > 0 && amount && (
              <div className="bg-slate-50 rounded-xl p-3 space-y-1">
                <p className="text-xs text-slate-500 font-medium">Cost Preview</p>
                <p className="text-sm">Total: ₹{amount} ÷ {totalHeads} heads = <strong>₹{perPersonPreview}/person</strong></p>
              </div>
            )}
          </div>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full h-11 bg-[#064E3B] hover:bg-[#065f46] rounded-xl font-bold"
          >
            {submitting ? "Updating..." : "Update Session"}
          </Button>
        </DialogContent>
      </Dialog>
    );
  };

  // Add Expense (for birthday cakes, etc.) - with guest support
  const AddExpenseModal = () => {
    const [description, setDescription] = useState("");
    const [amount, setAmount] = useState("");
    const [attendees, setAttendees] = useState({});
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
      const initial = {};
      users.forEach(u => {
        initial[u.id] = { selected: false, guests: 0 };
      });
      setAttendees(initial);
    }, [users]);

    const toggleMember = (userId) => {
      setAttendees(prev => ({
        ...prev,
        [userId]: { 
          ...prev[userId], 
          selected: !prev[userId]?.selected,
          guests: !prev[userId]?.selected ? 0 : prev[userId]?.guests || 0
        }
      }));
    };

    const setGuestCount = (userId, count) => {
      const guestCount = Math.max(0, parseInt(count) || 0);
      setAttendees(prev => ({
        ...prev,
        [userId]: { ...prev[userId], guests: guestCount }
      }));
    };

    const setMany = (ids, selected) => {
      setAttendees(prev => {
        const updated = { ...prev };
        ids.forEach(id => {
          updated[id] = { selected, guests: selected ? (prev[id]?.guests || 0) : 0 };
        });
        return updated;
      });
    };

    const selectedCount = Object.values(attendees).filter(a => a?.selected).length;
    const totalGuests = Object.values(attendees).reduce((sum, a) => sum + (a?.selected ? (a?.guests || 0) : 0), 0);
    const totalHeads = selectedCount + totalGuests;
    const perPersonPreview = totalHeads > 0 && amount ? (parseFloat(amount) / totalHeads).toFixed(2) : "0.00";

    const handleSubmit = async () => {
      if (!description || !amount || selectedCount === 0) {
        toast.error("All fields are required and select at least one member");
        return;
      }
      setSubmitting(true);
      try {
        const attendeesList = Object.entries(attendees)
          .filter(([_, data]) => data?.selected)
          .map(([userId, data]) => ({
            user_id: userId,
            guest_count: data?.guests || 0
          }));

        await apiClient.post("/expenses", {
          description,
          amount: parseFloat(amount),
          attendees: attendeesList,
        });
        toast.success("Expense added successfully");
        setShowAddExpense(false);
        fetchAllData();
      } catch (error) {
        toast.error("Failed to add expense");
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <Dialog open={showAddExpense} onOpenChange={setShowAddExpense}>
        <DialogContent className="sm:max-w-md rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Add Expense</DialogTitle>
            <DialogDescription>Add a shared expense (e.g., birthday cake)</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                data-testid="expense-description-input"
                placeholder="e.g., Birthday cake for Saurabh"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="h-11 rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label>Amount (₹)</Label>
              <Input
                data-testid="expense-amount-input"
                type="number"
                placeholder="Enter total amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="h-11 rounded-xl"
              />
            </div>
            <AttendeeSelector
              users={users}
              attendees={attendees}
              label={`Split Among (${selectedCount} members${totalGuests > 0 ? ` + ${totalGuests} guests` : ''})`}
              onToggle={toggleMember}
              onGuestChange={setGuestCount}
              onSetMany={setMany}
              testIdPrefix="expense"
            />
            
            {/* Preview calculation */}
            {totalHeads > 0 && amount && (
              <div className="bg-slate-50 rounded-xl p-3 space-y-1">
                <p className="text-xs text-slate-500 font-medium">Cost Preview</p>
                <p className="text-sm">
                  Total: ₹{amount} ÷ {totalHeads} heads = <strong>₹{perPersonPreview}/person</strong>
                </p>
                {totalGuests > 0 && (
                  <p className="text-xs text-slate-500">
                    Members bringing guests will pay for themselves + their guests
                  </p>
                )}
              </div>
            )}
          </div>
          <Button
            data-testid="expense-submit-btn"
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full h-11 bg-[#064E3B] hover:bg-[#065f46] rounded-xl font-bold"
          >
            {submitting ? "Adding..." : "Add Expense"}
          </Button>
        </DialogContent>
      </Dialog>
    );
  };

  // Add Post
  const AddPostModal = () => {
    const [title, setTitle] = useState("");
    const [content, setContent] = useState("");
    const [url, setUrl] = useState("");
    const [sendEmail, setSendEmail] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async () => {
      if (!title || !content) {
        toast.error("Title and content are required");
        return;
      }
      setSubmitting(true);
      try {
        await apiClient.post("/posts", {
          title,
          content,
          url: url || null,
          send_email: sendEmail
        });
        toast.success(`Post created successfully${sendEmail ? ' and emails sent to all members' : ''}`);
        setShowAddPost(false);
        fetchAllData();
      } catch (error) {
        toast.error("Failed to create post");
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <Dialog open={showAddPost} onOpenChange={setShowAddPost}>
        <DialogContent className="sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Create Post</DialogTitle>
            <DialogDescription>Share an announcement with the tribe</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                data-testid="post-title-input"
                placeholder="e.g., House warming party"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="h-11 rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label>Content</Label>
              <Textarea
                data-testid="post-content-input"
                placeholder="Write your announcement..."
                value={content}
                onChange={(e) => setContent(e.target.value)}
                className="min-h-24 rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label>Link (Optional)</Label>
              <Input
                data-testid="post-url-input"
                placeholder="https://..."
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                className="h-11 rounded-xl"
              />
            </div>
            <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-xl border border-blue-200">
              <Checkbox
                id="sendEmailPost"
                checked={sendEmail}
                onCheckedChange={setSendEmail}
              />
              <Label htmlFor="sendEmailPost" className="text-sm text-blue-800 cursor-pointer">
                <Mail className="w-4 h-4 inline mr-1" />
                Send email notification to all members
              </Label>
            </div>
          </div>
          <Button
            data-testid="post-submit-btn"
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full h-11 bg-[#064E3B] hover:bg-[#065f46] rounded-xl font-bold"
          >
            {submitting ? "Posting..." : sendEmail ? "Create Post & Send Email" : "Create Post"}
          </Button>
        </DialogContent>
      </Dialog>
    );
  };

  // Edit Post Modal
  const EditPostModal = () => {
    const [title, setTitle] = useState(editingPost?.title || "");
    const [content, setContent] = useState(editingPost?.content || "");
    const [url, setUrl] = useState(editingPost?.url || "");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
      if (editingPost) {
        setTitle(editingPost.title || "");
        setContent(editingPost.content || "");
        setUrl(editingPost.url || "");
      }
    }, [editingPost]);

    const handleSubmit = async () => {
      if (!title || !content) {
        toast.error("Title and content are required");
        return;
      }
      setSubmitting(true);
      try {
        await apiClient.put(`/posts/${editingPost.id}`, {
          title,
          content,
          url: url || null,
        });
        toast.success("Post updated successfully");
        setShowEditPost(false);
        setEditingPost(null);
        fetchAllData();
      } catch (error) {
        toast.error("Failed to update post");
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <Dialog open={showEditPost} onOpenChange={(open) => { setShowEditPost(open); if (!open) setEditingPost(null); }}>
        <DialogContent className="sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Edit Post</DialogTitle>
            <DialogDescription>Update your announcement</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                data-testid="edit-post-title-input"
                placeholder="e.g., House warming party"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="h-11 rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label>Content</Label>
              <Textarea
                data-testid="edit-post-content-input"
                placeholder="Write your announcement..."
                value={content}
                onChange={(e) => setContent(e.target.value)}
                className="min-h-24 rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label>Link (Optional)</Label>
              <Input
                data-testid="edit-post-url-input"
                placeholder="https://..."
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                className="h-11 rounded-xl"
              />
            </div>
          </div>
          <Button
            data-testid="edit-post-submit-btn"
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full h-11 bg-[#064E3B] hover:bg-[#065f46] rounded-xl font-bold"
          >
            {submitting ? "Updating..." : "Update Post"}
          </Button>
        </DialogContent>
      </Dialog>
    );
  };

  const deleteUser = async (userId) => {
    try {
      await apiClient.delete(`/users/${userId}`);
      toast.success("User deleted");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete user");
    }
  };

  const deleteLocation = async (locationId) => {
    try {
      await apiClient.delete(`/turf-locations/${locationId}`);
      toast.success("Location deleted");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete location");
    }
  };

  const deletePayment = async (paymentId) => {
    try {
      await apiClient.delete(`/payments/${paymentId}`);
      toast.success("Payment deleted and balance adjusted");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete payment");
    }
  };

  const deleteSession = async (sessionId) => {
    try {
      await apiClient.delete(`/sessions/${sessionId}`);
      toast.success("Session deleted and amounts refunded");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete session");
    }
  };

  const deleteExpense = async (expenseId) => {
    try {
      await apiClient.delete(`/expenses/${expenseId}`);
      toast.success("Expense deleted and amounts refunded");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete expense");
    }
  };

  const deletePost = async (postId) => {
    try {
      await apiClient.delete(`/posts/${postId}`);
      toast.success("Post deleted");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete post");
    }
  };

  // Confirm delete handler
  const handleConfirmDelete = async () => {
    const { type, id } = deleteConfirm;
    switch (type) {
      case 'user':
        await deleteUser(id);
        break;
      case 'location':
        await deleteLocation(id);
        break;
      case 'payment':
        await deletePayment(id);
        break;
      case 'session':
        await deleteSession(id);
        break;
      case 'expense':
        await deleteExpense(id);
        break;
      case 'post':
        await deletePost(id);
        break;
      default:
        break;
    }
  };

  // Open delete confirmation dialog
  const openDeleteConfirm = (type, id, name, title, description) => {
    setDeleteConfirm({
      open: true,
      type,
      id,
      name,
      title,
      description
    });
  };

  const viewUserDetails = async (userId) => {
    setLoadingUserDetail(true);
    setShowUserDetail(true);
    try {
      const response = await apiClient.get(`/admin/user/${userId}/details`);
      setSelectedUserDetail(response.data);
    } catch (error) {
      toast.error("Failed to load user details");
      setShowUserDetail(false);
    } finally {
      setLoadingUserDetail(false);
    }
  };

  // User Detail Modal
  const UserDetailModal = () => {
    if (!showUserDetail) return null;
    
    const data = selectedUserDetail;
    
    return (
      <Dialog open={showUserDetail} onOpenChange={setShowUserDetail}>
        <DialogContent className="sm:max-w-2xl rounded-2xl max-h-[90vh] overflow-y-auto">
          {loadingUserDetail ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-[#064E3B] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : data ? (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#064E3B] rounded-full flex items-center justify-center text-white font-bold">
                    {data.user.name.charAt(0)}
                  </div>
                  {data.user.name}'s Activity
                </DialogTitle>
                <DialogDescription>
                  {data.user.phone} {data.user.email && `• ${data.user.email}`}
                  {data.user.date_of_birth && ` • DOB: ${data.user.date_of_birth.split('-').reverse().join('-')}`}
                </DialogDescription>
                <div className="pt-3 flex flex-wrap items-center gap-2">
                  <LedgerDownloadButton
                    userId={data.user.id}
                    userName={data.user.name}
                    variant="outline"
                    size="sm"
                    label="Download Ledger (PDF)"
                    testIdPrefix={`admin-ledger-download-${data.user.id}`}
                  />
                  <Button
                    size="sm"
                    onClick={() => setSettleUser(data.user)}
                    disabled={Math.abs(Number(data.user.balance || 0)) < 0.005}
                    className="bg-[#059669] hover:bg-[#047857] text-white"
                    data-testid={`admin-settle-btn-${data.user.id}`}
                  >
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Settle Balance
                  </Button>
                </div>
              </DialogHeader>
              
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 py-4">
                <div className="bg-slate-50 rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold text-[#064E3B]">{data.summary.total_sessions_played}</p>
                  <p className="text-xs text-slate-500">Sessions Played</p>
                </div>
                <div className="bg-green-50 rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold text-green-600">₹{data.summary.total_payments_made}</p>
                  <p className="text-xs text-slate-500">Total Paid</p>
                </div>
                <div className="bg-red-50 rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold text-red-600">₹{data.summary.total_session_contribution + data.summary.total_expense_contribution}</p>
                  <p className="text-xs text-slate-500">Total Contribution</p>
                </div>
                <div className={`rounded-xl p-3 text-center ${data.summary.current_balance > 0 ? 'bg-red-50' : data.summary.current_balance < 0 ? 'bg-green-50' : 'bg-slate-50'}`}>
                  <p className={`text-2xl font-bold ${data.summary.current_balance > 0 ? 'text-red-600' : data.summary.current_balance < 0 ? 'text-green-600' : 'text-slate-500'}`}>
                    ₹{Math.abs(data.summary.current_balance).toFixed(2)}
                  </p>
                  <p className="text-xs text-slate-500">
                    {data.summary.current_balance > 0 ? 'You Owe' : data.summary.current_balance < 0 ? 'Owes You' : 'Settled'}
                  </p>
                </div>
              </div>
              
              {/* Sessions History */}
              <div className="space-y-3">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <CalendarIcon className="w-4 h-4" />
                  Sessions History ({data.sessions.length})
                </h3>
                
                {data.sessions.length === 0 ? (
                  <p className="text-slate-500 text-sm py-4 text-center">No sessions attended yet</p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {data.sessions.map((s) => (
                      <div
                        key={s.id}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-100"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <MapPin className="w-3 h-3 text-[#064E3B]" />
                            <span className="font-medium text-sm text-slate-900">{s.turf_location_name}</span>
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5">
                            {new Date(s.date).toLocaleDateString('en-IN', {
                              weekday: 'short',
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-red-600">-₹{s.user_charge.toFixed(2)}</p>
                          {s.user_guest_count > 0 && (
                            <p className="text-xs text-amber-600">+{s.user_guest_count} guest{s.user_guest_count > 1 ? 's' : ''}</p>
                          )}
                          <p className="text-xs text-slate-400">of ₹{s.total_amount} total</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Payments History */}
              {data.payments.length > 0 && (
                <div className="space-y-3 mt-4">
                  <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                    <CreditCard className="w-4 h-4" />
                    Payments Made ({data.payments.length})
                  </h3>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {data.payments.map((p) => (
                      <div
                        key={p.id}
                        className="flex items-center justify-between p-3 bg-green-50 rounded-xl border border-green-100"
                      >
                        <div>
                          <p className="font-medium text-sm text-slate-900">{p.payment_mode}</p>
                          <p className="text-xs text-slate-500">
                            {new Date(p.created_at).toLocaleDateString('en-IN', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </p>
                        </div>
                        <p className="font-bold text-green-600">+₹{p.amount.toFixed(2)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Expenses History */}
              {data.expenses.length > 0 && (
                <div className="space-y-3 mt-4">
                  <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                    <Cake className="w-4 h-4" />
                    Shared Expenses ({data.expenses.length})
                  </h3>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {data.expenses.map((e) => (
                      <div
                        key={e.id}
                        className="flex items-center justify-between p-3 bg-amber-50 rounded-xl border border-amber-100"
                      >
                        <div>
                          <p className="font-medium text-sm text-slate-900">{e.description}</p>
                          <p className="text-xs text-slate-500">
                            {new Date(e.created_at).toLocaleDateString('en-IN', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </p>
                        </div>
                        <p className="font-bold text-red-600">-₹{e.per_person_amount.toFixed(2)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-4 border-[#064E3B] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#064E3B] rounded-xl flex items-center justify-center">
              <span className="text-[#BEF264] font-bold text-lg">TT</span>
            </div>
            <div>
              <h1 className="font-heading font-bold text-lg text-slate-900">Turf Tribe Admin</h1>
              <p className="text-xs text-slate-500">Welcome, {user?.name}</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            data-testid="logout-btn"
            onClick={logout}
            className="text-red-600 border-red-200 hover:bg-red-50"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </header>

      {/* Birthdays Banner */}
      {upcomingBirthdays.length > 0 && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-100 px-4 py-3">
          <div className="max-w-6xl mx-auto flex items-center gap-3">
            <Cake className="w-5 h-5 text-amber-600" />
            <div className="flex-1 overflow-x-auto">
              <div className="flex gap-4">
                {upcomingBirthdays.slice(0, 5).map((b) => (
                  <span key={b.user_id} className="text-sm text-amber-800 whitespace-nowrap">
                    <strong>{b.name}</strong> - {b.birthday_date} ({b.days_until === 0 ? "Today!" : `${b.days_until}d`})
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border rounded-xl p-1 mb-6 flex-wrap">
            <TabsTrigger value="dashboard" data-testid="tab-dashboard" className="rounded-lg data-[state=active]:bg-[#064E3B] data-[state=active]:text-white">
              <BarChart3 className="w-4 h-4 mr-2" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users" className="rounded-lg data-[state=active]:bg-[#064E3B] data-[state=active]:text-white">
              <Users className="w-4 h-4 mr-2" />
              Users
            </TabsTrigger>
            <TabsTrigger value="locations" data-testid="tab-locations" className="rounded-lg data-[state=active]:bg-[#064E3B] data-[state=active]:text-white">
              <MapPin className="w-4 h-4 mr-2" />
              Locations
            </TabsTrigger>
            <TabsTrigger value="payments" data-testid="tab-payments" className="rounded-lg data-[state=active]:bg-[#064E3B] data-[state=active]:text-white">
              <CreditCard className="w-4 h-4 mr-2" />
              Payments
            </TabsTrigger>
            <TabsTrigger value="sessions" data-testid="tab-sessions" className="rounded-lg data-[state=active]:bg-[#064E3B] data-[state=active]:text-white">
              <CalendarIcon className="w-4 h-4 mr-2" />
              Sessions
            </TabsTrigger>
            <TabsTrigger value="expenses" data-testid="tab-expenses" className="rounded-lg data-[state=active]:bg-[#064E3B] data-[state=active]:text-white">
              <Cake className="w-4 h-4 mr-2" />
              Expenses
            </TabsTrigger>
            <TabsTrigger value="posts" data-testid="tab-posts" className="rounded-lg data-[state=active]:bg-[#064E3B] data-[state=active]:text-white">
              <FileText className="w-4 h-4 mr-2" />
              Posts
            </TabsTrigger>
            <TabsTrigger value="audit" data-testid="tab-audit" className="rounded-lg data-[state=active]:bg-[#064E3B] data-[state=active]:text-white">
              <History className="w-4 h-4 mr-2" />
              Activity Log
            </TabsTrigger>
            <TabsTrigger value="specialities" data-testid="tab-specialities" className="rounded-lg data-[state=active]:bg-[#064E3B] data-[state=active]:text-white">
              <Sparkles className="w-4 h-4 mr-2" />
              Specialities
            </TabsTrigger>
          </TabsList>

          {/* Dashboard/Financial Summary Tab */}
          <TabsContent value="dashboard" className="animate-fade-in">
            <div className="space-y-6">
              {/* Financial Overview Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Amount to Collect from Users */}
                <Card className="rounded-2xl border-slate-200 overflow-hidden">
                  <CardContent className="p-0">
                    <div className="bg-gradient-to-br from-green-500 to-green-600 p-5 text-white">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-green-100 text-sm font-medium">To Collect from Users</p>
                          <p className="text-3xl font-heading font-black mt-1">
                            ₹{financialSummary?.users_owe_to_admin?.toFixed(2) || '0.00'}
                          </p>
                          <p className="text-green-100 text-xs mt-2">Users with pending dues</p>
                        </div>
                        <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                          <TrendingUp className="w-7 h-7 text-white" />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Amount to Return to Users */}
                <Card className="rounded-2xl border-slate-200 overflow-hidden">
                  <CardContent className="p-0">
                    <div className="bg-gradient-to-br from-red-500 to-red-600 p-5 text-white">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-red-100 text-sm font-medium">To Return to Users</p>
                          <p className="text-3xl font-heading font-black mt-1">
                            ₹{financialSummary?.admin_owes_to_users?.toFixed(2) || '0.00'}
                          </p>
                          <p className="text-red-100 text-xs mt-2">Excess balance with users</p>
                        </div>
                        <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                          <TrendingDown className="w-7 h-7 text-white" />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Net Position */}
                <Card className="rounded-2xl border-slate-200 overflow-hidden">
                  <CardContent className="p-0">
                    <div className={`p-5 text-white ${
                      (financialSummary?.surplus_with_admin || 0) >= 0 
                        ? 'bg-gradient-to-br from-[#064E3B] to-[#065f46]' 
                        : 'bg-gradient-to-br from-amber-500 to-amber-600'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white/80 text-sm font-medium">
                            {(financialSummary?.surplus_with_admin || 0) >= 0 ? 'Net Receivable' : 'Net Payable'}
                          </p>
                          <p className="text-3xl font-heading font-black mt-1">
                            ₹{Math.abs(financialSummary?.surplus_with_admin || 0).toFixed(2)}
                          </p>
                          <p className="text-white/80 text-xs mt-2">
                            {(financialSummary?.surplus_with_admin || 0) >= 0 
                              ? 'Collect - Return = Net' 
                              : 'You need to collect more'}
                          </p>
                        </div>
                        <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                          <PiggyBank className="w-7 h-7 text-white" />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Stats Overview */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="rounded-2xl border-slate-200">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                        <Users className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-slate-900">{financialSummary?.total_members || 0}</p>
                        <p className="text-xs text-slate-500">Total Members</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="rounded-2xl border-slate-200">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                        <Wallet className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-slate-900">₹{financialSummary?.total_payments_received?.toFixed(0) || 0}</p>
                        <p className="text-xs text-slate-500">Total Received</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="rounded-2xl border-slate-200">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                        <CalendarIcon className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-slate-900">₹{financialSummary?.total_session_expenses?.toFixed(0) || 0}</p>
                        <p className="text-xs text-slate-500">Session Expenses</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="rounded-2xl border-slate-200">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                        <Cake className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-slate-900">₹{financialSummary?.total_other_expenses?.toFixed(0) || 0}</p>
                        <p className="text-xs text-slate-500">Other Expenses</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Member Balances Summary */}
              <Card className="rounded-2xl border-slate-200">
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-[#064E3B]" />
                    Member Balances Overview
                  </CardTitle>
                  <CardDescription>Quick view of all member balances (from admin's perspective)</CardDescription>
                </CardHeader>
                <CardContent>
                  {users.length === 0 ? (
                    <p className="text-slate-500 text-center py-6">No members added yet</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {users.map((u) => (
                        <div
                          key={u.id}
                          className={`flex items-center justify-between p-3 rounded-xl border ${
                            u.balance < 0 
                              ? 'bg-green-50 border-green-100' 
                              : u.balance > 0
                                ? 'bg-red-50 border-red-100'
                                : 'bg-slate-50 border-slate-100'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                              u.balance < 0 ? 'bg-green-500' : u.balance > 0 ? 'bg-red-500' : 'bg-slate-400'
                            }`}>
                              {u.name.charAt(0)}
                            </div>
                            <span className="font-medium text-slate-900 text-sm">{u.name}</span>
                          </div>
                          <div className="text-right">
                            <p className={`font-bold ${
                              u.balance < 0 ? 'text-green-600' : u.balance > 0 ? 'text-red-600' : 'text-slate-500'
                            }`}>
                              ₹{Math.abs(u.balance).toFixed(2)}
                            </p>
                            <p className="text-xs text-slate-500">
                              {u.balance < 0 ? 'Owes you' : u.balance > 0 ? 'You owe' : 'Settled'}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Email Notifications Status */}
              <Card className={`rounded-2xl ${emailStatus?.email_configured ? 'border-green-200 bg-green-50/30' : 'border-amber-200 bg-amber-50/30'}`}>
                <CardHeader className="pb-3">
                  <CardTitle className="font-heading flex items-center gap-2 text-lg">
                    <Mail className={`w-5 h-5 ${emailStatus?.email_configured ? 'text-green-600' : 'text-amber-600'}`} />
                    Email Notifications
                    {emailStatus?.email_configured ? (
                      <span className="ml-auto flex items-center gap-1 text-sm font-normal text-green-600">
                        <CheckCircle className="w-4 h-4" /> Active
                      </span>
                    ) : (
                      <span className="ml-auto flex items-center gap-1 text-sm font-normal text-amber-600">
                        <AlertCircle className="w-4 h-4" /> Not Configured
                      </span>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  {emailStatus?.email_configured ? (
                    <p className="text-sm text-green-700">
                      Members will automatically receive email notifications for sessions, expenses, and payments.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      <p className="text-sm text-amber-700">
                        Email notifications are not configured. Members won't receive email updates for activities.
                      </p>
                      <div className="bg-white rounded-xl p-4 border border-amber-200">
                        <p className="text-sm font-medium text-slate-700 mb-2">To enable email notifications:</p>
                        <ol className="text-sm text-slate-600 list-decimal list-inside space-y-1">
                          <li>Enable 2-Factor Authentication on your Gmail account</li>
                          <li>Go to <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="text-[#064E3B] underline">Google App Passwords</a></li>
                          <li>Generate an App Password for "Mail"</li>
                          <li>Add SMTP_USER (your Gmail) and SMTP_PASSWORD (app password) to backend/.env</li>
                          <li>Restart the backend service</li>
                        </ol>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Player Statistics */}
              <PlayerStatsWidget limit={5} showViewMore={true} />
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="animate-fade-in">
            <Card className="rounded-2xl border-slate-200">
              <CardHeader className="flex flex-row items-center justify-between gap-4 flex-wrap">
                <div>
                  <CardTitle className="font-heading">User Management</CardTitle>
                  <CardDescription>Add and manage team members</CardDescription>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Export Buttons */}
                  {users.length > 0 && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleExportUsers('pdf')}
                        disabled={selectedUserIds.length === 0 || exportingUsers}
                        className="rounded-xl"
                        data-testid="export-users-pdf-btn"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        PDF ({selectedUserIds.length})
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleExportUsers('excel')}
                        disabled={selectedUserIds.length === 0 || exportingUsers}
                        className="rounded-xl"
                        data-testid="export-users-excel-btn"
                      >
                        <FileSpreadsheet className="w-4 h-4 mr-2" />
                        Excel ({selectedUserIds.length})
                      </Button>
                    </>
                  )}
                  <Button
                    data-testid="add-user-btn"
                    onClick={() => setShowAddUser(true)}
                    className="bg-[#064E3B] hover:bg-[#065f46] rounded-xl"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add User
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {users.length === 0 ? (
                  <div className="empty-state">
                    <Users className="w-12 h-12 mb-3" />
                    <p>No members yet. Add your first member!</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Select All Row */}
                    <div className="flex items-center gap-3 p-3 bg-slate-100 rounded-xl border border-slate-200">
                      <Checkbox
                        id="select-all-users"
                        checked={selectedUserIds.length === users.length && users.length > 0}
                        onCheckedChange={toggleSelectAllUsers}
                        data-testid="select-all-users-checkbox"
                      />
                      <label htmlFor="select-all-users" className="text-sm font-medium text-slate-600 cursor-pointer">
                        {selectedUserIds.length === users.length 
                          ? `All ${users.length} member(s) selected` 
                          : `Select all ${users.length} members`
                        }
                      </label>
                    </div>
                    
                    {users.map((u) => (
                      <div
                        key={u.id}
                        data-testid={`user-card-${u.id}`}
                        className={`flex items-center justify-between p-4 rounded-xl border card-hover cursor-pointer hover:border-[#064E3B]/30 ${
                          selectedUserIds.includes(u.id) 
                            ? 'bg-[#064E3B]/5 border-[#064E3B]/30' 
                            : 'bg-slate-50 border-slate-100'
                        }`}
                      >
                        {/* Checkbox for selection */}
                        <div className="flex items-center gap-4">
                          <Checkbox
                            checked={selectedUserIds.includes(u.id)}
                            onCheckedChange={() => toggleUserSelection(u.id)}
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`select-user-${u.id}`}
                          />
                          <div 
                            className="flex items-center gap-4 flex-1"
                            onClick={() => viewUserDetails(u.id)}
                          >
                            {u.has_photo || u.photo_url ? (
                              <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-[#064E3B]/20 flex-shrink-0">
                                <img
                                  src={u.photo_url || memberPhotoUrl(u.id)}
                                  alt={u.name}
                                  loading="lazy"
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            ) : (
                              <div className="avatar-circle bg-[#064E3B]">
                                {u.name.charAt(0).toUpperCase()}
                              </div>
                            )}
                            <div>
                              <p className="font-semibold text-slate-900">{u.name}</p>
                              <p className="text-sm text-slate-500">{u.phone}</p>
                              {u.email && (
                                <p className="text-xs text-slate-400">{u.email}</p>
                              )}
                              {u.date_of_birth && (
                                <p className="text-xs text-slate-400">DOB: {u.date_of_birth.split('-').reverse().join('-')}</p>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="text-right mr-2" onClick={() => viewUserDetails(u.id)}>
                            <p className="text-xs text-slate-500">Balance</p>
                            <p className={`font-bold text-lg ${u.balance > 0 ? 'text-red-600' : u.balance < 0 ? 'text-green-600' : 'text-slate-500'}`}>
                              {u.balance > 0 ? '-' : u.balance < 0 ? '+' : ''}₹{Math.abs(u.balance).toFixed(2)}
                            </p>
                            <p className="text-xs text-[#064E3B] font-medium">View Details →</p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            data-testid={`edit-user-${u.id}`}
                            onClick={(e) => { e.stopPropagation(); setEditingUser(u); setShowEditUser(true); }}
                            className="text-slate-500 hover:text-[#064E3B] hover:bg-slate-100"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            data-testid={`delete-user-${u.id}`}
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              openDeleteConfirm('user', u.id, u.name, 'Delete User', `This will permanently delete ${u.name} and all their data.`);
                            }}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Locations Tab */}
          <TabsContent value="locations" className="animate-fade-in">
            <Card className="rounded-2xl border-slate-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="font-heading">Turf Locations</CardTitle>
                  <CardDescription>Manage pickleball turf locations</CardDescription>
                </div>
                <Button
                  data-testid="add-location-btn"
                  onClick={() => setShowAddLocation(true)}
                  className="bg-[#064E3B] hover:bg-[#065f46] rounded-xl"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Location
                </Button>
              </CardHeader>
              <CardContent>
                {turfLocations.length === 0 ? (
                  <div className="empty-state">
                    <MapPin className="w-12 h-12 mb-3" />
                    <p>No locations yet. Add your first turf!</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {turfLocations.map((loc) => (
                      <div
                        key={loc.id}
                        data-testid={`location-card-${loc.id}`}
                        className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-100 card-hover"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-[#064E3B]/10 rounded-lg flex items-center justify-center">
                            <MapPin className="w-5 h-5 text-[#064E3B]" />
                          </div>
                          <span className="font-medium text-slate-900">{loc.name}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            data-testid={`edit-location-${loc.id}`}
                            onClick={() => { setEditingLocation(loc); setShowEditLocation(true); }}
                            className="text-slate-500 hover:text-[#064E3B] hover:bg-slate-100"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            data-testid={`delete-location-${loc.id}`}
                            onClick={() => openDeleteConfirm('location', loc.id, loc.name, 'Delete Location', `This will permanently delete the location "${loc.name}".`)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments" className="animate-fade-in">
            <Card className="rounded-2xl border-slate-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="font-heading">Payment Management</CardTitle>
                  <CardDescription>Record payments received from users</CardDescription>
                </div>
                <Button
                  data-testid="add-payment-btn"
                  onClick={() => setShowAddPayment(true)}
                  className="bg-[#064E3B] hover:bg-[#065f46] rounded-xl"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Record Payment
                </Button>
              </CardHeader>
              <CardContent>
                {payments.length === 0 ? (
                  <div className="empty-state">
                    <CreditCard className="w-12 h-12 mb-3" />
                    <p>No payments recorded yet.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {payments.map((p) => (
                      <div
                        key={p.id}
                        data-testid={`payment-card-${p.id}`}
                        className="flex items-center justify-between p-4 bg-green-50 rounded-xl border border-green-100 border-l-4 border-l-green-500"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                            <Plus className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <p className="font-semibold text-slate-900">{p.user_name}</p>
                            <p className="text-sm text-slate-500">{p.payment_mode}</p>
                            <p className="text-xs text-slate-400">
                              {new Date(p.created_at).toLocaleDateString('en-IN', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <p className="text-xl font-bold text-green-600 mr-2">+₹{p.amount.toFixed(2)}</p>
                          <Button
                            variant="ghost"
                            size="icon"
                            data-testid={`edit-payment-${p.id}`}
                            onClick={() => { setEditingPayment(p); setShowEditPayment(true); }}
                            className="text-slate-500 hover:text-[#064E3B] hover:bg-slate-100"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            data-testid={`delete-payment-${p.id}`}
                            onClick={() => openDeleteConfirm('payment', p.id, p.user_name, 'Delete Payment', `This will delete the ₹${p.amount} payment from ${p.user_name} and adjust their balance.`)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Sessions Tab */}
          <TabsContent value="sessions" className="animate-fade-in">
            <Card className="rounded-2xl border-slate-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="font-heading">Session Management</CardTitle>
                  <CardDescription>Create and manage pickleball sessions</CardDescription>
                </div>
                <Button
                  data-testid="add-session-btn"
                  onClick={() => setShowAddSession(true)}
                  className="bg-[#064E3B] hover:bg-[#065f46] rounded-xl"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Session
                </Button>
              </CardHeader>
              <CardContent>
                {sessions.length === 0 ? (
                  <div className="empty-state">
                    <CalendarIcon className="w-12 h-12 mb-3" />
                    <p>No sessions yet. Create your first session!</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {sessions.map((s) => {
                      const totalGuests = s.attendees_detail?.reduce((sum, a) => sum + (a.guest_count || 0), 0) || 0;
                      return (
                        <div
                          key={s.id}
                          data-testid={`session-card-${s.id}`}
                          className="p-5 bg-slate-50 rounded-2xl border border-slate-200 session-card"
                        >
                          <div className="flex items-start justify-between mb-4">
                            <div>
                              <h3 className="font-bold text-lg text-slate-900">{s.turf_location_name}</h3>
                              <p className="text-slate-500">
                                {new Date(s.date).toLocaleDateString('en-IN', {
                                  weekday: 'long',
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })}
                              </p>
                            </div>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                data-testid={`edit-session-${s.id}`}
                                onClick={() => { setEditingSession(s); setShowEditSession(true); }}
                                className="text-slate-500 hover:text-[#064E3B] hover:bg-slate-100"
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                data-testid={`delete-session-${s.id}`}
                                onClick={() => openDeleteConfirm('session', s.id, s.turf_location_name, 'Delete Session', `This will delete the session at ${s.turf_location_name} on ${s.date} and refund all attendees.`)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          <div className="grid grid-cols-3 gap-4 mb-4">
                            <div className="bg-white p-3 rounded-xl">
                              <p className="text-xs text-slate-500 uppercase tracking-wide">Total Amount</p>
                              <p className="text-xl font-bold text-slate-900">₹{s.total_amount.toFixed(2)}</p>
                            </div>
                            <div className="bg-white p-3 rounded-xl">
                              <p className="text-xs text-slate-500 uppercase tracking-wide">Per Person</p>
                              <p className="text-xl font-bold text-red-600">₹{s.per_person_amount.toFixed(2)}</p>
                            </div>
                            <div className="bg-white p-3 rounded-xl">
                              <p className="text-xs text-slate-500 uppercase tracking-wide">Total Heads</p>
                              <p className="text-xl font-bold text-slate-900">{s.total_attendees || s.attendee_names.length}</p>
                              {totalGuests > 0 && (
                                <p className="text-xs text-slate-400">{s.attendee_names.length} + {totalGuests} guests</p>
                              )}
                            </div>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Attendees:</p>
                            <div className="flex flex-wrap gap-2">
                              {s.attendees_detail?.length > 0 ? (
                                s.attendees_detail.map((att, i) => (
                                  <span key={i} className="attendee-chip">
                                    {att.user_name}
                                    {att.guest_count > 0 && (
                                      <span className="ml-1 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">
                                        +{att.guest_count} guest{att.guest_count > 1 ? 's' : ''}
                                      </span>
                                    )}
                                    <span className="ml-1 text-xs text-slate-400">(₹{att.amount_charged.toFixed(0)})</span>
                                  </span>
                                ))
                              ) : (
                                s.attendee_names.map((name, i) => (
                                  <span key={i} className="attendee-chip">{name}</span>
                                ))
                              )}
                            </div>
                          </div>
                          {/* Record Scores Button */}
                          <div className="mt-4 pt-4 border-t border-slate-200">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => { setSelectedSessionForScores(s); setShowScoreModal(true); }}
                              className="w-full border-[#064E3B] text-[#064E3B] hover:bg-[#064E3B] hover:text-white"
                            >
                              <Trophy className="w-4 h-4 mr-2" />
                              Record Match Scores
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Expenses Tab */}
          <TabsContent value="expenses" className="animate-fade-in">
            <Card className="rounded-2xl border-slate-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="font-heading">Expense Management</CardTitle>
                  <CardDescription>Manage shared expenses (birthday cakes, etc.)</CardDescription>
                </div>
                <Button
                  data-testid="add-expense-btn"
                  onClick={() => setShowAddExpense(true)}
                  className="bg-[#064E3B] hover:bg-[#065f46] rounded-xl"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Expense
                </Button>
              </CardHeader>
              <CardContent>
                {expenses.length === 0 ? (
                  <div className="empty-state">
                    <Cake className="w-12 h-12 mb-3" />
                    <p>No expenses yet.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {expenses.map((e) => {
                      const totalGuests = e.attendees_detail?.reduce((sum, a) => sum + (a.guest_count || 0), 0) || 0;
                      return (
                        <div
                          key={e.id}
                          data-testid={`expense-card-${e.id}`}
                          className="p-5 bg-slate-50 rounded-2xl border border-slate-200"
                        >
                          <div className="flex items-start justify-between mb-4">
                            <div>
                              <h3 className="font-bold text-lg text-slate-900">{e.description}</h3>
                              <p className="text-sm text-slate-500">
                                {new Date(e.created_at).toLocaleDateString('en-IN', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })}
                              </p>
                            </div>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                data-testid={`delete-expense-${e.id}`}
                                onClick={() => openDeleteConfirm('expense', e.id, e.description, 'Delete Expense', `This will delete "${e.description}" and refund all participants.`)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          <div className="grid grid-cols-3 gap-4 mb-4">
                            <div className="bg-white p-3 rounded-xl">
                              <p className="text-xs text-slate-500 uppercase tracking-wide">Total Amount</p>
                              <p className="text-xl font-bold text-slate-900">₹{e.amount.toFixed(2)}</p>
                            </div>
                            <div className="bg-white p-3 rounded-xl">
                              <p className="text-xs text-slate-500 uppercase tracking-wide">Per Person</p>
                              <p className="text-xl font-bold text-red-600">₹{e.per_person_amount.toFixed(2)}</p>
                            </div>
                            <div className="bg-white p-3 rounded-xl">
                              <p className="text-xs text-slate-500 uppercase tracking-wide">Total Heads</p>
                              <p className="text-xl font-bold text-slate-900">{e.total_heads || e.member_names.length}</p>
                              {totalGuests > 0 && (
                                <p className="text-xs text-slate-400">{e.member_names.length} + {totalGuests} guests</p>
                              )}
                            </div>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Split Among:</p>
                            <div className="flex flex-wrap gap-2">
                              {e.attendees_detail?.length > 0 ? (
                                e.attendees_detail.map((att, i) => (
                                  <span key={i} className="attendee-chip">
                                    {att.user_name}
                                    {att.guest_count > 0 && (
                                      <span className="ml-1 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">
                                        +{att.guest_count} guest{att.guest_count > 1 ? 's' : ''}
                                      </span>
                                    )}
                                    <span className="ml-1 text-xs text-slate-400">(₹{att.amount_charged.toFixed(0)})</span>
                                  </span>
                                ))
                              ) : (
                                e.member_names.map((name, i) => (
                                  <span key={i} className="attendee-chip">{name}</span>
                                ))
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Posts Tab */}
          <TabsContent value="posts" className="animate-fade-in">
            <Card className="rounded-2xl border-slate-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="font-heading">Posts & Announcements</CardTitle>
                  <CardDescription>Share updates with the tribe</CardDescription>
                </div>
                <Button
                  data-testid="add-post-btn"
                  onClick={() => setShowAddPost(true)}
                  className="bg-[#064E3B] hover:bg-[#065f46] rounded-xl"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Post
                </Button>
              </CardHeader>
              <CardContent>
                {posts.length === 0 ? (
                  <div className="empty-state">
                    <FileText className="w-12 h-12 mb-3" />
                    <p>No posts yet. Create your first announcement!</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {posts.map((p) => (
                      <div
                        key={p.id}
                        data-testid={`post-card-${p.id}`}
                        className="p-5 bg-white rounded-2xl border border-slate-200 card-hover"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="font-bold text-lg text-slate-900">{p.title}</h3>
                            <p className="text-sm text-slate-500">
                              By {p.author_name} • {new Date(p.created_at).toLocaleDateString('en-IN')}
                            </p>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              data-testid={`edit-post-${p.id}`}
                              onClick={() => { setEditingPost(p); setShowEditPost(true); }}
                              className="text-slate-500 hover:text-[#064E3B] hover:bg-slate-100"
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              data-testid={`delete-post-${p.id}`}
                              onClick={() => openDeleteConfirm('post', p.id, p.title, 'Delete Post', `This will permanently delete the post "${p.title}".`)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        <p className="text-slate-700 mb-3">{p.content}</p>
                        {p.url && (
                          <a
                            href={p.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#064E3B] hover:underline text-sm font-medium"
                          >
                            {p.url}
                          </a>
                        )}
                        <div className="flex items-center gap-4 mt-4 pt-4 border-t border-slate-100">
                          <span className="flex items-center gap-1 text-sm text-slate-500">
                            <Heart className="w-4 h-4" /> {p.like_count} likes
                          </span>
                          <span className="flex items-center gap-1 text-sm text-slate-500">
                            <MessageCircle className="w-4 h-4" /> {p.comments.length} comments
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Activity Log Tab */}
          <TabsContent value="audit" className="animate-fade-in">
            <AuditLogTab />
          </TabsContent>

          {/* Specialities Tab */}
          <TabsContent value="specialities" className="animate-fade-in">
            <SpecialitiesTab />
          </TabsContent>
        </Tabs>
      </main>

      {/* Modals */}
      <AddUserModal />
      <AddLocationModal />
      <AddPaymentModal />
      <AddSessionModal />
      <AddExpenseModal />
      <AddPostModal />
      <EditUserModal />
      <EditLocationModal />
      <EditPaymentModal />
      <EditSessionModal />
      <EditPostModal />
      <UserDetailModal />
      
      {/* Score Recording Modal */}
      <ScoreRecordingModal
        open={showScoreModal}
        onOpenChange={setShowScoreModal}
        session={selectedSessionForScores}
        attendees={selectedSessionForScores?.attendees_detail || []}
        onSaved={fetchAllData}
      />
      
      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteConfirm.open}
        onOpenChange={(open) => setDeleteConfirm(prev => ({ ...prev, open }))}
        onConfirm={handleConfirmDelete}
        title={deleteConfirm.title}
        description={deleteConfirm.description}
        itemName={deleteConfirm.name}
      />

      {/* Settle Balance Dialog */}
      <SettleBalanceDialog
        open={!!settleUser}
        onOpenChange={(open) => { if (!open) setSettleUser(null); }}
        user={settleUser}
        onSettled={async () => {
          await fetchAllData();
          if (settleUser?.id) {
            await viewUserDetails(settleUser.id);
          }
        }}
      />
    </div>
  );
};

export default AdminDashboard;
